/**
 * The example clip on the outer side of the hero: a 9:16 frame with the one
 * shared `<video>` (see `example-video.ts`), the sound, enlarge and play
 * controls, the caption, and the three notes around it — what the clip
 * starts with, what it is made for, and what this example is.
 *
 * The controls announce their state through `aria-pressed` and a label that
 * says what pressing will do; the stylesheet picks the icon half from the
 * same attribute. Under a pointer that cannot hover, they are always shown.
 */

import type { ReactNode } from 'react';

import type { PublicEarlyAccessCopy } from './copy.js';
import type { ExampleVideo as ExampleVideoController } from './example-video.js';
import { EnlargeIcon, PlayPauseIcon, SoundIcon } from './icons.js';

export function ExampleVideo({
  copy,
  video,
  onEnlarge,
}: {
  readonly copy: PublicEarlyAccessCopy;
  readonly video: ExampleVideoController;
  readonly onEnlarge: (from: HTMLElement) => void;
}): ReactNode {
  return (
    <figure className="cs-preview" aria-label={copy.a11yFigure}>
      <div className="cs-frame">
        <div className="cs-frame-field" aria-hidden="true" />
        <div className="cs-frame-media" ref={video.inlineHost} />
        {video.element}
        <button
          type="button"
          className="cs-frame-sound cs-sound-toggle"
          aria-pressed={!video.muted}
          aria-label={video.muted ? copy.unmute : copy.mute}
          onClick={video.toggleSound}
        >
          <SoundIcon />
        </button>
        <button
          type="button"
          className="cs-frame-enlarge"
          aria-label={copy.enlarge}
          onClick={(event) => {
            onEnlarge(event.currentTarget);
          }}
        >
          <EnlargeIcon />
        </button>
        <button
          type="button"
          className="cs-frame-play cs-play-toggle"
          aria-pressed={video.playing}
          aria-label={video.playing ? copy.pause : copy.play}
          onClick={video.togglePlay}
        >
          <PlayPauseIcon />
        </button>
        <figcaption className="cs-frame-caption">
          <strong>{copy.fTitle}</strong>
          <span>{copy.fMeta}</span>
        </figcaption>
      </div>
      <div className="cs-notes" hidden>
        <div className="cs-note cs-note-1">
          <span>{copy.n1l}</span>
          <strong>{copy.n1v}</strong>
        </div>
        <div className="cs-note cs-note-2">
          <span>{copy.n2l}</span>
          <strong>{copy.n2v}</strong>
        </div>
        <div className="cs-note cs-note-3">
          <span>{copy.n3l}</span>
          <strong>{copy.n3v}</strong>
        </div>
      </div>
    </figure>
  );
}
